import { type Dispatch, type FC, useEffect } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { Drawer, Snackbar } from '@crehana/ui';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import {
  AttributesUploadProvider,
  useAttributesUploadContext,
} from '@/views/AdminDashboard/components/AttributesDrawer/UserAttributesUploadContext';

import Footer from './components/Footer';
import Header from './components/Header';
import Stepper from './components/Stepper';
import { AMPLITUDE } from './constants';
import { Step1, Step2, Step3, Step4 } from './Steps';

interface AttributesDrawerProps {
  isDark?: boolean;
  isOpenAttributesDrawer: boolean;
  setIsOpenAttributesDrawer: Dispatch<boolean>;
}

const AttributesDrawerContent: FC<AttributesDrawerProps> = ({
  isDark,
  isOpenAttributesDrawer,
  setIsOpenAttributesDrawer,
}) => {
  const { logEvent } = useAmplitude();
  const router = useRouter();
  const { slug } = router.query as { slug: string };

  const {
    currentStep,
    handleNextStep,
    handleBackStep,
    snackbar,
    setSnackbar,
    resetDrawer,
  } = useAttributesUploadContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!isOpenAttributesDrawer) resetDrawer();

    if (isOpenAttributesDrawer) {
      logEvent({
        event: AMPLITUDE.EVENT.LOAD.ATTRIBUTES,
        data: {
          Page: router.route.includes('subir-usuarios')
            ? AMPLITUDE.PAGE.USER_UPLOAD
            : AMPLITUDE.PAGE.ATTRIBUTES,
          Organization: slug,
          Success: true,
          ...getSourceEventParams(),
          Type: AMPLITUDE.TYPE.START,
        },
      });
    }
  }, [isOpenAttributesDrawer, router.route, slug]);

  const closeDrawer = () => setIsOpenAttributesDrawer(false);

  const currentRender = {
    1: <Step1 isDark={isDark} />,
    2: <Step2 isDark={isDark} />,
    3: <Step3 isDark={isDark} />,
    4: <Step4 closeDrawer={closeDrawer} />,
  };

  return (
    <Drawer
      open={isOpenAttributesDrawer}
      className="flex flex-col h-full"
      onDismiss={closeDrawer}
      width={{ xs: '100vw', sm: '70vw', md: '40vw', lg: '40vw' }}
      isDark={isDark}
    >
      <Header isDark={isDark} closeDrawer={closeDrawer} />
      <div className="overflow-auto h-full py-24 px-32">
        <Stepper isDark={isDark} />
        <section
          className={clsx('flex flex-col', currentStep === 4 && 'h-full')}
        >
          {currentRender[currentStep as 1 | 2 | 3 | 4]}
        </section>
      </div>
      <Footer
        currentStep={currentStep}
        isDark={isDark}
        closeDrawer={closeDrawer}
        handleBackStep={handleBackStep}
        handleNextStep={handleNextStep}
      />
      <Snackbar
        position="top-right"
        isDark={isDark}
        dismissOnDelay={3000}
        dismissOnClickOutside
        onDismiss={() => setSnackbar({})}
        // biome-ignore lint/complexity/noBannedTypes: legacy type usage
        {...(snackbar as {})}
      />
    </Drawer>
  );
};

const AttributesDrawer: FC<AttributesDrawerProps> = ({
  isDark,
  isOpenAttributesDrawer,
  setIsOpenAttributesDrawer,
}) => {
  return (
    <AttributesUploadProvider>
      <AttributesDrawerContent
        isDark={isDark}
        isOpenAttributesDrawer={isOpenAttributesDrawer}
        setIsOpenAttributesDrawer={setIsOpenAttributesDrawer}
      />
    </AttributesUploadProvider>
  );
};

export default AttributesDrawer;
